import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Loader2, MessageCircle, ArrowRight } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Inbox() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inbox");
        setThreads(data.threads || []);
      } catch (err) {
        toast.error(errorMessage(err, "Could not load inbox"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Conversations</span>
      <h1 className="font-serif-display text-4xl md:text-5xl mt-2">Your inbox.</h1>
      <p className="mt-3 text-lg text-[#5c5c5c]">Only unlocked matches appear here.</p>

      {threads.length === 0 ? (
        <div className="mt-10 p-12 rounded-2xl bg-white border border-[#e5e0d8] card-elev text-center" data-testid="inbox-empty">
          <MessageCircle className="w-8 h-8 text-[#722f37] mx-auto" />
          <h3 className="font-serif-display text-2xl mt-4 text-[#722f37]">No conversations yet.</h3>
          <p className="mt-2 text-[#5c5c5c]">Unlock a match to start a conversation.</p>
          <div className="mt-6"><Link to="/matches" className="btn-primary" data-testid="inbox-go-matches">Go to matches</Link></div>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {threads.map((t) => {
            const u = t.other_user;
            const photo = u.photos?.[0];
            return (
              <Link
                key={t.match_id}
                to={`/chat/${t.match_id}`}
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-[#e5e0d8] card-elev hover:border-[#722f37] transition-colors"
                data-testid={`inbox-thread-${t.match_id}`}
              >
                <div className="w-16 h-16 rounded-full overflow-hidden bg-[#eae5d9] flex-shrink-0">
                  {photo?.data_url ? (
                    <img src={photo.data_url} alt={u.display_name} className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="font-serif-display text-xl text-[#722f37] truncate">{u.display_name}</h3>
                    {u.verified && <VerifiedBadge size={12} />}
                    <span className="text-xs text-[#5c5c5c] ml-auto">{formatWhen(t.last_message?.created_at || t.matched_at)}</span>
                  </div>
                  <p className="text-sm text-[#5c5c5c] truncate mt-1">
                    {t.last_message ? t.last_message.text : <span className="italic">You matched — say hello.</span>}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#722f37] flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
